import * as Sentry from '@sentry/react';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { BUTTON_PRIMARY } from '../ui';

interface PageErrorBoundaryProps {
  children: ReactNode;
  /**
   * The current path. A boundary that has caught stays caught, so it has to be
   * cleared on navigation — but clearing it by keying the boundary on the path
   * remounts every healthy page too, and a path holds more than the page: the
   * project view's own id sits in it, so picking a different project threw
   * away the whole screen and restarted every query on it.
   */
  path: string;
}

interface PageErrorBoundaryState {
  failed: boolean;
}

/**
 * Catches anything thrown while a page renders — including a chunk that would
 * not load after `lazyPage` has already spent its reload. Without it React
 * unmounts the whole remote and the host is left showing an empty panel.
 */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  override state: PageErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): PageErrorBoundaryState {
    return { failed: true };
  }

  override componentDidUpdate(previous: PageErrorBoundaryProps) {
    if (this.state.failed && previous.path !== this.props.path) {
      this.setState({ failed: false });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The boundary stops the error reaching window.onerror, which is what used
    // to report these, so hand it to Sentry here instead. A chunk that a deploy
    // has deleted under this tab reaches here too, and the `beforeSend` in
    // `main.tsx` drops it — see `isStaleChunkError`.
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  override render() {
    if (!this.state.failed) {
      return this.props.children;
    }

    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className="text-lg font-semibold text-hilevel-slate">This page didn&apos;t load</h1>
        <p className="mt-2 text-sm text-gray-600">
          Reloading usually fixes it. If it keeps happening, let us know what you were doing.
        </p>
        <button
          type="button"
          className={`${BUTTON_PRIMARY} mt-4`}
          onClick={() => window.location.reload()}
        >
          Reload the page
        </button>
      </div>
    );
  }
}
